import Description from '@/_components/template/Description'
import GotoEndpoint from '@/_components/template/GotoEndpoint'
import Heading from '@/_components/template/Heading'
import SectionBlock from '@/_components/template/SectionBlock'
import SectionHeading from '@/_components/template/SectionHeading'
import { ABOUT_PAGE_CONTENT } from '@/content/siteContent'
import { usePageSeo } from '@/lib/seo'
import { BsPerson } from 'react-icons/bs'

export default function AboutPage() {
    usePageSeo({
        title: 'About Tuhin Kairi | Full-Stack Developer and AI Engineer',
        description: ABOUT_PAGE_CONTENT.intro,
        keywords: ['About Tuhin Kairi', 'AI engineer profile', 'full-stack developer experience', 'React Next.js developer', 'engineering portfolio'],
    })

    return (
        <section className="relative tracking-wider sm:px-6">
            <main className="container mx-auto px-4 pt-32 pb-20 sm:px-5 sm:pt-36 lg:px-0 lg:pt-20">
                <SectionBlock className="grid-cols-1 gap-10 border-b border-border py-12 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
                    <div className="space-y-6 text-center lg:text-left">
                        <SectionHeading number="02" content={ABOUT_PAGE_CONTENT.eyebrow} className="justify-center pb-2 lg:justify-start" />
                        <Heading className="mx-auto max-w-[20ch] lg:mx-0">
                            {ABOUT_PAGE_CONTENT.title}
                        </Heading>
                        <Description className="mx-auto max-w-3xl leading-7 text-secondary sm:text-lg lg:mx-0 lg:w-full">
                            {ABOUT_PAGE_CONTENT.intro}
                        </Description>
                    </div>
                    <div className="rounded-[2rem] border border-border bg-surface p-6 sm:p-8">
                        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-secondary">Core Strengths</p>
                        <ul className="mt-6 space-y-4 text-base leading-7 text-primary sm:text-lg">
                            {ABOUT_PAGE_CONTENT.highlights.map((item) => (
                                <li key={item} className="border-b border-border/80 pb-4 last:border-b-0 last:pb-0">
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                </SectionBlock>

                <SectionBlock className="grid-cols-1 gap-8 py-12 lg:grid-cols-2 lg:gap-12">
                    {ABOUT_PAGE_CONTENT.story.map((item) => (
                        <div key={item.key} className="rounded-[2rem] border border-border bg-ground p-6 sm:p-8 h-full">
                            {item.image ? <img loading="lazy" src={item.image} alt={item.title} className="mb-6 w-36 mx-auto" /> : <BsPerson size={50} className='p-2 rounded-full border mx-auto mb-6' />}
                            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-secondary">{item.type}</p>
                            <Heading className="mx-auto mt-2 max-w-[20ch] lg:mx-0">{item.title}</Heading>
                            <Description className="mb-0 mt-2 w-full leading-7 text-secondary sm:text-lg">
                                {item.description}
                            </Description>
                        </div>
                    ))}
                </SectionBlock>

                <SectionBlock className="grid-cols-1 gap-6 border-t border-border py-12 sm:grid-cols-2 lg:grid-cols-3">
                    {ABOUT_PAGE_CONTENT.metrics.map((metric) => (
                        <div key={metric.label} className="rounded-[2rem] border border-border bg-surface p-6 text-center sm:p-8 lg:text-left">
                            <p className="text-4xl font-heading text-primary sm:text-5xl">{metric.value}</p>
                            <p className="mt-3 text-sm uppercase tracking-[0.3em] text-secondary">{metric.label}</p>
                        </div>
                    ))}
                </SectionBlock>

                <SectionBlock className="grid-cols-1 gap-8 border-t border-border py-12 lg:grid-cols-2">
                    {ABOUT_PAGE_CONTENT.skillGroups.map((group) => (
                        <div key={group.title} className="rounded-[2rem] border border-border bg-ground p-6 sm:p-8">
                            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-secondary">{group.title}</p>
                            <div className="mt-6 flex flex-wrap gap-3">
                                {group.items.map((item) => (
                                    <span key={item} className="rounded-full border border-border px-4 py-2 text-sm text-primary sm:text-base">
                                        {item}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </SectionBlock>

                <SectionBlock className="grid-cols-1 gap-8 border-t border-border py-12">
                    <div className="text-center lg:text-left">
                        <SectionHeading number="03" content="Experience" className="justify-center pb-4 lg:justify-start" />
                        <Heading className="mx-auto  lg:mx-0">Timeline and measurable impact</Heading>
                    </div>
                    <div className="grid gap-8">
                        {ABOUT_PAGE_CONTENT.experience.map((item) => (
                            <article key={`${item.company}-${item.role}`} className="rounded-[2rem] border border-border bg-surface p-6 sm:p-8">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                                    <div>
                                        <h2 className="text-3xl font-heading text-primary sm:text-4xl">{item.company}</h2>
                                        <p className="mt-2 text-lg text-secondary">{item.role}</p>
                                    </div>
                                    <p className="text-sm uppercase tracking-[0.3em] text-secondary">{item.timeline}</p>
                                </div>
                                <Description className="mb-0 w-full max-w-none leading-7 text-secondary sm:text-lg">
                                    {item.summary}
                                </Description>
                                <ul className="mt-6 space-y-3 text-base leading-7 text-primary sm:text-lg">
                                    {item.highlights.map((highlight) => (
                                        <li key={highlight} className="border-b border-border/80 pb-3 last:border-b-0 last:pb-0">
                                            {highlight}
                                        </li>
                                    ))}
                                </ul>
                            </article>
                        ))}
                    </div>
                </SectionBlock>

                <div className="pt-6 text-center lg:text-left">
                    <GotoEndpoint content="back to home" endpoint="/" className="inline-flex items-center sm:text-lg" />
                </div>
            </main>
        </section>
    )
}
